import { Input, Select, Textarea } from '@/design/components'
import type { CaseOption } from '../types'

/** Wspólny stan formularza wpisu (modale „Rejestruj czas" i edycji). */
export interface TimeEntryFormState {
  caseId: string
  workDate: string
  hours: string
  minutes: string
  description: string
  billable: boolean
}

/** 90 → { hours: '1', minutes: '30' } — rozbicie minut z API na pola formularza. */
export function splitMinutes(total: number): { hours: string; minutes: string } {
  return { hours: String(Math.floor(total / 60)), minutes: String(total % 60) }
}

/** Pola „godziny" + „minuty" → minuty łącznie; `null`, gdy poza zakresem 1..1440. */
export function combineMinutes(hours: string, minutes: string): number | null {
  const h = hours.trim() === '' ? 0 : Number(hours)
  const m = minutes.trim() === '' ? 0 : Number(minutes)
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || m < 0 || m > 59) return null
  const total = h * 60 + m
  return total >= 1 && total <= 1440 ? total : null
}

export interface TimeEntryFormFieldsProps {
  form: TimeEntryFormState
  onChange: (next: TimeEntryFormState) => void
  /** Sprawy z `/api/cases` — sortujemy po sygnaturze przy renderze. */
  cases: CaseOption[]
  disabled?: boolean
}

/** Pola formularza wpisu czasu — układ jak w modalach modułu `deadlines`. */
export function TimeEntryFormFields({ form, onChange, cases, disabled = false }: TimeEntryFormFieldsProps) {
  const set = <K extends keyof TimeEntryFormState>(key: K, value: TimeEntryFormState[K]) =>
    onChange({ ...form, [key]: value })

  const sortedCases = [...cases].sort((a, b) => a.signature.localeCompare(b.signature, 'pl'))

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <Select
          label="Sprawa"
          value={form.caseId}
          disabled={disabled}
          onChange={(event) => set('caseId', event.target.value)}
          options={[
            { value: '', label: 'wybierz sprawę…' },
            ...sortedCases.map((item) => ({
              value: String(item.id),
              label: `${item.signature} · ${item.client_name}`,
            })),
          ]}
        />
      </div>
      <Input
        label="Data"
        type="date"
        value={form.workDate}
        disabled={disabled}
        onChange={(event) => set('workDate', event.target.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Godziny"
          type="number"
          min={0}
          max={24}
          value={form.hours}
          disabled={disabled}
          onChange={(event) => set('hours', event.target.value)}
        />
        <Input
          label="Minuty"
          type="number"
          min={0}
          max={59}
          step={5}
          value={form.minutes}
          disabled={disabled}
          onChange={(event) => set('minutes', event.target.value)}
        />
      </div>
      <div className="col-span-2">
        <Textarea
          label="Opis czynności"
          rows={3}
          value={form.description}
          disabled={disabled}
          placeholder="np. Projekt pisma procesowego, analiza akt…"
          onChange={(event) => set('description', event.target.value)}
        />
      </div>
      <label className="col-span-2 flex cursor-pointer items-center gap-2 text-base">
        <input
          type="checkbox"
          className="h-[14px] w-[14px] accent-navy-900"
          checked={form.billable}
          disabled={disabled}
          onChange={(event) => set('billable', event.target.checked)}
        />
        <span className="font-medium text-ink">Rozliczalny</span>
        <span className="t-small text-muted">pozycja trafi na zestawienie dla klienta</span>
      </label>
    </div>
  )
}
